import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../../api/axios';
import { getLoanBalanceForSellerNetSheet } from '../../../utils/calculator';

// A lightweight reusable hook that wraps Google Places AutocompleteService + PlacesService
// Exposes: query, setQuery, suggestions, isLoading, activeIndex, suggestionRefs,
// handlers: onChange, onKeyDown, onBlur, selectSuggestion

export default function usePlaceAutocomplete({ 
  country = 'us', 
  minLength = 2, 
  onSelect, 
  mode = 'seller', 
  showToaster, 
  page =null, 
  maincompanyid = null 
} = {}) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  const debounceRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const requestIdRef = useRef(0);
  const suggestionItemRefs = useRef([]);

  const ensureMapsLoaded = useCallback((key) => {
    return new Promise((resolve, reject) => {
      if (typeof window !== 'undefined' && window.google && window.google.maps && window.google.maps.places) {
        try {
          if (!autocompleteServiceRef.current) autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
          if (!placesServiceRef.current) placesServiceRef.current = new window.google.maps.places.PlacesService(document.createElement('div'));
          return resolve();
        } catch (err) { return reject(err); }
      }
      const existing = Array.from(document.getElementsByTagName('script')).find(s => s.src && s.src.includes('maps.googleapis.com/maps/api/js'));
      if (existing) {
        if (typeof window !== 'undefined' && window.google && window.google.maps && window.google.maps.places) {
          try {
            if (!autocompleteServiceRef.current) autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
            if (!placesServiceRef.current) placesServiceRef.current = new window.google.maps.places.PlacesService(document.createElement('div'));
            return resolve();
          } catch (err) { return reject(err); }
        }
        const onLoad = () => {
          try {
            autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
            placesServiceRef.current = new window.google.maps.places.PlacesService(document.createElement('div'));
            cleanup();
            resolve();
          } catch (err) { cleanup(); reject(err); }
        };
        const onError = (e) => { cleanup(); reject(e); };
        const cleanup = () => {
          existing.removeEventListener('load', onLoad);
          existing.removeEventListener('error', onError);
        };
        existing.addEventListener('load', onLoad);
        existing.addEventListener('error', onError);
        return;
      }
      const script = document.createElement('script');
      const k = encodeURIComponent(process.env.REACT_APP_GOOGLE_API_KEY || '');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${k}&libraries=places,geometry&language=en`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        try {
          autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
          placesServiceRef.current = new window.google.maps.places.PlacesService(document.createElement('div'));
          resolve();
        } catch (err) { reject(err); }
      };
      script.onerror = (e) => reject(e);
      document.head.appendChild(script);
    });
  }, []);

  const fetchPlacePredictions = useCallback(async (input) => {
    const key = process.env.REACT_APP_GOOGLE_API_KEY;
    setIsLoading(true);
    await ensureMapsLoaded(key);
    const rid = ++requestIdRef.current;
    try {
      autocompleteServiceRef.current.getPlacePredictions({ input, componentRestrictions: { country }, types: ['address'] }, (predictions, status) => {
        if (rid !== requestIdRef.current) return;
        if (status === window.google.maps.places.PlacesServiceStatus.OK && Array.isArray(predictions)) {
          setSuggestions(predictions.slice(0, 5));
          setActiveIndex(-1);
          setError(null);
        } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          setSuggestions([]);
        } else {
          setSuggestions([]);
          setError(status);
        }
        setIsLoading(false);
      });
    } catch (e) {
      setIsLoading(false);
      setError(e?.message || e);
    }
  }, [ensureMapsLoaded, country]);

  const handleChange = (value) => {
    setQuery(value);
    setError(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value || value.length < minLength) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(() => fetchPlacePredictions(value), 300);
    setActiveIndex(-1);
  };

  useEffect(() => {
    const el = suggestionItemRefs.current[activeIndex];
    if (el && el.scrollIntoView) {
      try { el.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } catch { }
    }
  }, [activeIndex]);

  const handleKeyDown = (e) => {
    if (!suggestions || suggestions.length === 0) return;
    const max = suggestions.length - 1;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex((prev) => (prev < max ? prev + 1 : max)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex((prev) => (prev > 0 ? prev - 1 : -1)); }
    else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex <= max) { e.preventDefault(); selectSuggestion(suggestions[activeIndex]); }
    } else if (e.key === 'Escape') { setSuggestions([]); setActiveIndex(-1); }
  };

  const handleBlur = () => { setTimeout(() => setSuggestions([]), 150); };

  const selectSuggestion = async (item) => {
    try {
      const description = item.description || item.prediction || '';
      setQuery(description);
      setSuggestions([]);
      // load place details
      const placeId = item.place_id;
      const key = process.env.REACT_APP_GOOGLE_API_KEY;
      if (!key || !placeId) return null;
      await ensureMapsLoaded(key);
      // wait for placesService
      const waitForPlacesService = async (timeout = 2000, interval = 50) => {
        const start = Date.now();
        while (!placesServiceRef.current && Date.now() - start < timeout) { await new Promise((r) => setTimeout(r, interval)); }
        if (!placesServiceRef.current) throw new Error('PlacesService not available');
      };
      await waitForPlacesService(2000, 50);

      const getPlaceDetails = (placeId, fields = ['address_components', 'formatted_address', 'geometry']) => {
        return new Promise((resolve, reject) => {
          try {
            if (placesServiceRef.current && typeof placesServiceRef.current.getDetails === 'function') {
              placesServiceRef.current.getDetails({ placeId, fields }, (place, status) => {
                if (status === (window.google && window.google.maps && window.google.maps.places && window.google.maps.places.PlacesServiceStatus) && status !== window.google.maps.places.PlacesServiceStatus.OK) {
                  return reject(new Error('PlacesService status: ' + status));
                }
                if (!place) return reject(new Error('No place returned'));
                return resolve(place);
              });
              return;
            }
            return reject(new Error('No supported Places API available'));
          } catch (err) { return reject(err); }
        });
      };

      const place = await getPlaceDetails(placeId);

      if (!place) return null;
      const comps = place.address_components || [];
      const find = (type) => { const c = comps.find((cc) => cc.types.includes(type)); return c ? c.long_name : ''; };
      const findShort = (type) => { const c = comps.find((cc) => cc.types.includes(type)); return c ? c.short_name : ''; };
      const street_number = find('street_number');
      const route_long = find('route');
      const route_short = findShort('route');
      const locality = find('locality') || find('sublocality') || find('neighborhood');
      const state = findShort('administrative_area_level_1');
      const county = findShort('administrative_area_level_2');
      const postal = find('postal_code');
      const site_unit = find('subpremise') || '';
     // const street = [street_number, route_long].filter(Boolean).join(' ');

      let payload = {
        zip: postal || '',
        state: state || '',
        county: county.replace(' County', '') || '',
        street_no: street_number || '',
        route: route_short || route_long || '',
        route_long: route_long || route_short || '',
        site_unit: site_unit || '',
        city: locality || '',
        formatted_address: place.formatted_address || description,
        geometry: place.geometry || null,
        
      };
      /*
            // enrichment: call backend to resolve city/state/ttb and compute loan balance where possible
            try {
              const resp = await api.get('/get-city-state-for-zip', { params: { zip: payload.zip || '', street_no: payload.street_no || '', route: payload.route || '', route_long: payload.route_long || '', site_unit: payload.site_unit || '', city: payload.city || '', calc: 'seller' } });
              const data = resp && resp.data ? resp.data : null;
              if (data) {
                payload.city = data.city || payload.city || '';
                payload.companyid = data.companyid || '';
                payload.state = data.state_name || payload.state || '';
                // attach raw enrichment data for callers who want it
                const zipData = data.data;
                payload.enrichment = zipData;
                // attempt to compute loan balance if ttb + loan info present
                const ttb = zipData && zipData.ttb ? zipData.ttb : zipData || null;
                payload.enrichment.taxAmount = ttb.taxAmount;
                
                if (mode === 'seller') {
                  if (ttb && (ttb.loanAmount || zipData?.loanRate || zipData?.loanDate)) {
                    try {
                      const loanBalance = getLoanBalanceForSellerNetSheet(ttb.loanAmount, zipData?.loanRate || 0, 30, ttb.loanDate);
                      if (loanBalance) payload.loanBalance = loanBalance;
                    } catch (e) {
                      // ignore loan balance errors
                    }
                  }
                }
              }
            } catch (e) {
              // enrichment failed; continue returning basic payload
            }
      */
      try {
        let resp = null;
        if(page === "register"){
          resp = await api.get('/get-address-details', { params: { zip: payload.zip , maincompanyid: maincompanyid } });
        } else {
          resp = await api.get('/get-city-state-for-zip', { params: { zip: payload.zip || '', street_no: payload.street_no || '', route: payload.route || '', route_long: payload.route_long || '', site_unit: payload.site_unit || '', city: payload.city || '', calc: 'seller' } });
        }
        
        if (resp?.status === 400) {
          //console.warn("ZIP lookup bad request:", resp.data?.message);
          const msg = resp?.data?.message || 'Invalid ZIP lookup';
          if (typeof showToaster === 'function') {
            try { showToaster({ message: msg, visible: true, type: 'error' }); } catch (e) { /* ignore */ }
          } else {
            // fallback to alert if no toaster available
            try { alert(msg); } catch (e) { /* ignore */ }
          }
          if (typeof onSelect === 'function') onSelect(payload);
          return payload;
        }
        // Stop processing if API failed
        /* if (resp?.data?.status === 'fail') {
          console.warn("ZIP lookup failed:", resp.data?.message);
          setError(resp.data.message);
          const msg = resp.data.message || 'ZIP lookup failed';
          if (typeof showToaster === 'function') {
            try { showToaster({ message: msg, visible: true, type: 'error' }); } catch (e) {  }
          } else {
            try { alert(msg); } catch (e) {  }
          }
          if (typeof onSelect === 'function') onSelect(payload);
          return payload;
        } */

        // Otherwise continue normal parsing
        const data = resp.data;

        payload.city = payload.city || '';
        payload.state = payload.state || '';
        payload.county = payload.county || '';
        payload.zip = payload.zip || '';
        payload.mode = mode || '';
        

        const zipData = data.data;
        // payload.enrichment = zipData;

        if(page === "register"){
          payload.state = zipData?.state_name || '';
          payload.county = zipData?.county_name || '';
        }
        payload.enrichment = {
          ...(zipData || {}),
          mode: mode || ''
        };

        const ttb = zipData?.ttb || null;
        payload.enrichment.taxAmount = ttb?.taxAmount;


        if (mode === 'seller' && ttb?.loanAmount) {
          const loanBalance = getLoanBalanceForSellerNetSheet(ttb.loanAmount, zipData?.loanRate || 0, 30, ttb.loanDate);
          if (loanBalance) payload.loanBalance = loanBalance;
        }
      } catch (e) {
        try { showToaster({ message: 'The zip code you entered is incorrect or the State/County is not supported.', visible: true, type: 'error' }); } catch (e) { /* ignore */ }
        console.error("ZIP enrichment failed:", e);
      }


      // call optional onSelect callback (do not await)
      if (typeof onSelect === 'function') {
        try { onSelect(payload); } catch (e) { /* ignore */ }
      }
      return payload;
    } catch (err) {
      console.error('selectSuggestion failed', err);
      return null;
    }
  };

  useEffect(() => {
    return () => {
      try { if (debounceRef.current) clearTimeout(debounceRef.current); } catch (e) { }
    };
  }, []);

  return {
    query,
    setQuery,
    suggestions,
    isLoading,
    error,
    activeIndex,
    suggestionItemRefs,
    handleChange,
    handleKeyDown,
    handleBlur,
    selectSuggestion,
  };
}
